import type { ReactElement, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@erp/ui";
import type { BadgeProps } from "@erp/ui";

import { MoneyDisplay } from "@/components/money/money-display";
import { formatDateTime } from "@/lib/format";
import type { MoneyValue } from "@/lib/money/money";

/**
 * Reusable cell renderers for DataTable columns. Each is a plain function a
 * column def calls inside `cell:`; the ones that need the active locale return
 * a small component so hooks stay legal:
 *
 *   cell: (ctx) => dateTimeCell(ctx.getValue<string>())
 */

/** Plain text; nullish/empty renders an em dash placeholder. */
export function textCell(value: unknown): ReactNode {
  if (value === null || value === undefined || value === "") {
    return <span className="text-muted-foreground">&mdash;</span>;
  }
  return String(value);
}

function DateTimeCellContent({
  iso,
  options,
}: {
  iso: string;
  options?: Intl.DateTimeFormatOptions;
}) {
  const { i18n } = useTranslation();
  let formatted: string;
  try {
    formatted = formatDateTime(iso, i18n.language, options);
  } catch {
    // A malformed wire timestamp must not crash the whole table; show it raw.
    formatted = iso;
  }
  return <>{formatted}</>;
}

/**
 * RFC 3339 UTC timestamp rendered in the viewer's locale and time zone
 * (formatDateTime defaults: dateStyle "medium" + timeStyle "short").
 */
export function dateTimeCell(iso: string, options?: Intl.DateTimeFormatOptions): ReactElement {
  return <DateTimeCellContent iso={iso} options={options} />;
}

export type BadgeCellVariant = NonNullable<BadgeProps["variant"]>;

/**
 * Status badge. `variantMap` maps wire status codes to badge variants; unknown
 * codes fall back to "outline". Pass `label` with the translated status text —
 * the raw code is only the last-resort content.
 */
export function badgeCell(
  value: string,
  variantMap: Record<string, BadgeCellVariant>,
  label?: string,
): ReactElement {
  return <Badge variant={variantMap[value] ?? "outline"}>{label ?? value}</Badge>;
}

/**
 * Money cell — MoneyDisplay (never a JS number). Columns using it should set
 * meta { align: "end" }; MoneyDisplay already applies tabular figures and LTR
 * isolation itself.
 */
export function moneyCell(value: MoneyValue): ReactElement {
  return <MoneyDisplay value={value} />;
}
